import React from 'react';
import { gsap } from 'gsap';

import styles from './FlowingMenu.module.css';

const colors = ['#FF5B52', '#FFCB74', '#FF9BBC', '#79D6FF', '#A7A6FF'];

function FlowingMenu({ items = [] }) {
  const [activeIndex, setActiveIndex] = React.useState(null);

  return (
    <div className={styles.menuWrap}>
      <nav className={styles.menu}>
        {items.map((item, idx) => (
          <MenuItem 
            key={idx} 
            {...item} 
            index={idx} 
            color={colors[idx % colors.length]}
            isActive={activeIndex === idx}
            onActivate={() => setActiveIndex(idx)}
            onDeactivate={() => setActiveIndex(null)}
          />
        ))}
      </nav>
    </div>
  );
}

function MenuItem({ link, text, image, color, index, isActive, onActivate, onDeactivate }) {
  const itemRef = React.useRef(null);
  const marqueeRef = React.useRef(null);
  const marqueeInnerRef = React.useRef(null);
  const [isMobileActive, setIsMobileActive] = React.useState(false);
  const [isMobileDevice, setIsMobileDevice] = React.useState(false);

  const animationDefaults = { duration: 0.6, ease: 'expo' };

  const findClosestEdge = (mouseX, mouseY, width, height) => {
    const topEdgeDist = distMetric(mouseX, mouseY, width / 2, 0);
    const bottomEdgeDist = distMetric(mouseX, mouseY, width / 2, height);
    return topEdgeDist < bottomEdgeDist ? 'top' : 'bottom';
  };

  const distMetric = (x, y, x2, y2) => {
    const xDiff = x - x2;
    const yDiff = y - y2;
    return xDiff * xDiff + yDiff * yDiff;
  };

  const triggerAnimation = (x, y, width, height) => {
    if (!itemRef.current || !marqueeRef.current || !marqueeInnerRef.current) return;
    const edge = findClosestEdge(x, y, width, height);

    gsap
      .timeline({ defaults: animationDefaults })
      .set(marqueeRef.current, { y: edge === 'top' ? '-101%' : '101%' }, 0)
      .set(marqueeInnerRef.current, { y: edge === 'top' ? '101%' : '-101%' }, 0)
      .to([marqueeRef.current, marqueeInnerRef.current], { y: '0%' }, 0);
  };

  const removeAnimation = (x, y, width, height) => {
    if (!itemRef.current || !marqueeRef.current || !marqueeInnerRef.current) return;
    const edge = findClosestEdge(x, y, width, height);

    gsap
      .timeline({ defaults: animationDefaults })
      .to(marqueeRef.current, { y: edge === 'top' ? '-101%' : '101%' }, 0)
      .to(marqueeInnerRef.current, { y: edge === 'top' ? '101%' : '-101%' }, 0);
  };

  React.useEffect(() => {
    // 모바일 디바이스 감지
    const checkMobile = () => {
      const isMobile = window.matchMedia('(max-width: 1024px)').matches || 
                      'ontouchstart' in window || 
                      navigator.maxTouchPoints > 0;
      setIsMobileDevice(isMobile);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // 다른 아이템이 활성화되면 현재 아이템의 애니메이션 제거
  React.useEffect(() => {
    if (!isActive && isMobileActive && itemRef.current) {
      const rect = itemRef.current.getBoundingClientRect();
      removeAnimation(rect.width / 2, rect.height / 2, rect.width, rect.height);
      setIsMobileActive(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActive, isMobileActive]);

  const handleMouseEnter = ev => {
    if (!itemRef.current) return;
    const rect = itemRef.current.getBoundingClientRect();
    const x = ev.clientX - rect.left;
    const y = ev.clientY - rect.top;
    triggerAnimation(x, y, rect.width, rect.height);
  };

  const handleMouseLeave = ev => {
    if (!itemRef.current) return;
    const rect = itemRef.current.getBoundingClientRect();
    const x = ev.clientX - rect.left;
    const y = ev.clientY - rect.top;
    removeAnimation(x, y, rect.width, rect.height);
  };

  const handleClick = ev => {
    if (!isMobileDevice) return; // 데스크톱에서는 기본 동작 유지
    
    if (!itemRef.current) return;
    const rect = itemRef.current.getBoundingClientRect();
    
    if (!isMobileActive) {
      // 첫 번째 클릭: 애니메이션 트리거
      ev.preventDefault();
      ev.stopPropagation();
      
      // 클릭 좌표 가져오기
      const clientX = ev.clientX || (ev.touches && ev.touches[0]?.clientX) || rect.left + rect.width / 2;
      const clientY = ev.clientY || (ev.touches && ev.touches[0]?.clientY) || rect.top + rect.height / 2;
      const x = clientX - rect.left;
      const y = clientY - rect.top;
      
      triggerAnimation(x, y, rect.width, rect.height);
      setIsMobileActive(true);
      onActivate();
    } else {
      // 두 번째 클릭: 링크로 이동
      setIsMobileActive(false);
      onDeactivate();
      // 기본 링크 동작 허용 (preventDefault 호출 안 함)
    }
  };

  // 텍스트가 무한 반복되도록 충분히 많은 횟수로 반복 (두 세트로 나눠서 끊김 없이 반복)
  const marqueeItem = (
    <>
      <span style={{ color: color }}>{text}</span>
      <div className={styles.marqueeImg} style={{ backgroundImage: `url(${image})` }} />
    </>
  );

  const repeatedMarqueeContent = Array.from({ length: 10 }).map((_, idx) => (
    <React.Fragment key={idx}>
      {marqueeItem}
    </React.Fragment>
  ));

  return (
    <div className={styles.menuItem} ref={itemRef}>
      <a 
        className={styles.menuItemLink} 
        href={link} 
        onMouseEnter={handleMouseEnter} 
        onMouseLeave={handleMouseLeave}
        onClick={handleClick}
      >
        {text}
      </a>
      <div className={styles.marquee} ref={marqueeRef}>
        <div className={styles.marqueeInnerWrap} ref={marqueeInnerRef}>
          <div className={styles.marqueeInner} aria-hidden="true">
            {repeatedMarqueeContent}
            {repeatedMarqueeContent}
          </div>
        </div>
      </div>
    </div>
  );
}

export default FlowingMenu;
